const LOCAL_HOST_PATTERN = /^(localhost|127\.0\.0\.1)$/i;

const API_BASE_URL = (() => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL.replace(/\/$/, '');
  }
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && LOCAL_HOST_PATTERN.test(window.location.hostname)) {
    return 'http://localhost:5000';
  }
  return '';
})();

function buildUrl(path) {
  return new URL(`${API_BASE_URL}${path}`, typeof window !== 'undefined' ? window.location.origin : 'http://localhost').toString();
}

async function requestArchitect(path, options = {}) {
  const { method = 'GET', body, token } = options;
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (body) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(buildUrl(path), {
    method,
    headers: Object.keys(headers).length ? headers : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.error || 'Strategy Architect request failed.');
    error.payload = payload;
    throw error;
  }
  return payload;
}

export async function fetchStrategyArchitectSchema() {
  return requestArchitect('/api/strategy-architect/schema');
}

export async function fetchVixAiStatus() {
  return requestArchitect('/api/strategy-architect/ai-status');
}

export async function fetchVixAiTools(token) {
  return requestArchitect('/api/strategy-architect/vixai/tools', { token });
}

export async function runVixAiWebSearch({ query, count = 5 }, token) {
  return requestArchitect('/api/strategy-architect/vixai/tools/web-search', {
    method: 'POST',
    body: { query, count },
    token,
  });
}

export async function requestVixAiCodeDraft({ language = 'mq5', purpose = '', strategy = null, source = '', filename = '' }, token) {
  return requestArchitect('/api/strategy-architect/vixai/tools/code-draft', {
    method: 'POST',
    body: { language, purpose, strategy, source, filename },
    token,
  });
}

export async function generateStrategyDslDraft(prompt, token, context = {}) {
  return requestArchitect('/api/strategy-architect/draft', {
    method: 'POST',
    body: { prompt, context },
    token,
  });
}

export async function sendVixAiMessage({ message, dsl = null, context = {}, conversationId = null }, token) {
  return requestArchitect('/api/strategy-architect/vixai', {
    method: 'POST',
    body: { message, dsl, context, conversationId },
    token,
  });
}

export async function sendVixAiVerify({ message, intendedDsl = null, appliedPayload = null, context = {} }, token) {
  return requestArchitect('/api/strategy-architect/vixai/verify', {
    method: 'POST',
    body: { message, intendedDsl, appliedPayload, context },
    token,
  });
}

export async function listVixAiConversations(token) {
  return requestArchitect('/api/strategy-architect/vixai/conversations', { token });
}

export async function fetchVixAiConversation(conversationId, token) {
  return requestArchitect(`/api/strategy-architect/vixai/conversations/${conversationId}`, { token });
}

export async function validateStrategyDsl(dsl) {
  return requestArchitect('/api/strategy-architect/validate', {
    method: 'POST',
    body: { dsl },
  });
}

export async function compileStrategyDsl(dsl, token) {
  return requestArchitect('/api/strategy-architect/compile', {
    method: 'POST',
    body: { dsl },
    token,
  });
}

export async function createBuilderStrategyFromDsl(dsl, token) {
  return requestArchitect('/api/strategy-architect/create-builder-strategy', {
    method: 'POST',
    body: { dsl },
    token,
  });
}

export async function listStrategyDslDrafts(token) {
  return requestArchitect('/api/strategy-architect/drafts', { token });
}

export async function saveStrategyDslDraft({ dsl, prompt }, token) {
  return requestArchitect('/api/strategy-architect/drafts', {
    method: 'POST',
    body: { dsl, prompt },
    token,
  });
}

export async function fetchStrategyDslDraft(draftId, token) {
  return requestArchitect(`/api/strategy-architect/drafts/${draftId}`, { token });
}
